import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';

// Pages
import { LandingPage } from './pages/Landing';
import { Dashboard } from './pages/Dashboard';
import { HorsesPage } from './pages/Horses';
import { HorseProfile } from './pages/HorseProfile';
import { Reminders as RemindersPage } from './pages/Reminders';
import { GearPage } from './pages/Gear';
import { DocumentsPage } from './pages/Documents';
import { AccountPage } from './pages/Account';
import { AuthCallback } from './pages/AuthCallback';
import { RiderProfiles } from './pages/RiderProfiles';
import { Contracts } from './pages/Contracts';
import { ExpensesPage } from './pages/Expenses';
import { ReportsPage } from './pages/Reports';
import { GoalsPage } from './pages/Goals';
import { QRProfilePage } from './pages/QRProfile';
import { QRPublicPage } from './pages/QRPublic';
import { StableDashboard } from './pages/StableDashboard';
import { HelpPage } from './pages/Help';
import { TeamPage } from './pages/Team';

// Contexts
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ActiveAccountProvider } from './components/AccountSwitcher';
import { PWAInstallPrompt, OfflineIndicator } from './components/PWAComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Safe storage helper for Safari Private Browsing mode
const safeStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    } catch (e) {
      console.warn('Storage access denied (Private Browsing?)');
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      try {
        sessionStorage.setItem(key, value);
      } catch (e2) {
        console.warn('All storage denied - session will not persist across tabs');
      }
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {}
    try {
      sessionStorage.removeItem(key);
    } catch (e) {}
  }
};

// Helper to get active account header
export const getActiveAccountHeader = () => {
  // Use safe storage helper for iOS Safari compatibility
  const activeAccountId = safeStorage.getItem('activeAccountId');
  return activeAccountId ? { 'X-Active-Account': activeAccountId } : {};
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(safeStorage.getItem('token'));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // If user just logged out, don't auto-login
    if (window.location.search?.includes('logged_out=true') || sessionStorage.getItem('logging_out')) {
      sessionStorage.removeItem('logging_out');
      setUser(null);
      setToken(null);
      safeStorage.removeItem('token');
      setLoading(false);
      // Clean the URL
      window.history.replaceState({}, '', '/');
      return;
    }
    
    // ONLY check auth if we have a token in localStorage - NO auto-login from cookies
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
        // NO credentials: 'include' - prevents cookie-based auto-login
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        setToken(null);
        safeStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      // Simple fetch without credentials to avoid iOS Safari cloning issues
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.trim(), password })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      
      if (data.token) {
        safeStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        return { success: true };
      } else {
        throw new Error('No token received');
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      // Simple fetch without credentials to avoid iOS Safari cloning issues
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Registration failed');
      }

      const data = await response.json();
      
      if (data.token) {
        safeStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        return { success: true };
      } else {
        throw new Error('No token received');
      }
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    // Set a flag to prevent auto-login during this session
    try { sessionStorage.setItem('logging_out', 'true'); } catch (e) {}
    
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear all auth state
    setUser(null);
    setToken(null);
    safeStorage.removeItem('token');
    safeStorage.removeItem('activeAccountId');
    try { sessionStorage.clear(); } catch (e) {}
    
    // Clear any cookies we can access from JS
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    // Hard reload to ensure all state is cleared
    window.location.href = '/?logged_out=true';
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/auth/me`, {
        
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const setUserData = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, token, setUserData, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-forest border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/horses" element={<ProtectedRoute><HorsesPage /></ProtectedRoute>} />
      <Route path="/horses/:horseId/qr-profile" element={<ProtectedRoute><QRProfilePage /></ProtectedRoute>} />
      <Route path="/qr/:qrCodeId" element={<QRPublicPage />} />
      <Route path="/horses/:horseId" element={<ProtectedRoute><HorseProfile /></ProtectedRoute>} />
      <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
      <Route path="/gear" element={<ProtectedRoute><GearPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><Contracts /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
      <Route path="/stable-dashboard" element={<ProtectedRoute><StableDashboard /></ProtectedRoute>} />
      <Route path="/rider-profiles" element={<ProtectedRoute><RiderProfiles /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ActiveAccountProvider>
          <SubscriptionProvider>
            <OfflineIndicator />
            <Toaster position="top-right" richColors />
            <AppRouter />
            <PWAInstallPrompt />
          </SubscriptionProvider>
        </ActiveAccountProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
